// Sept 2026: versioned import so Shopify's CDN picks up edits to the card template immediately.
// Bump the ?v= whenever algolia-custom-hit.js changes. Exact pre-edit copy: algolia-custom.backup-20260903.js
import hitTemplate from './algolia-custom-hit.js?v=20260904a';
//import { infiniteHits } from './algolia-custom-infinite-hits.js';
import { initAlgoliaMobileDrawer } from './algolia-custom-drawer.js';
import { initRefinementListFade } from './algolia-refinement-list-fade.js';
import {
  facetStateSanitizeWidget,
  customerPreferencesWidget,
} from './algolia-my-size.js';
import {
  stripSuggestedRefinementsFromUrl,
  enhanceFacetParams,
  suggestedRefinementWidget,
} from './algolia-suggested-refinement.js';

if (typeof window !== 'undefined') {
  stripSuggestedRefinementsFromUrl();
}

const setSearchInputPlaceholder = () => {
  const searchInput = document.querySelector('#algolia-search-form .aa-Input');
  if (searchInput) {
    searchInput.placeholder = 'Search';
  }
  if (searchInput && !searchInput.classList.contains('blur-init')) {
    searchInput.addEventListener('blur', () => {
      searchInput.classList.add('blur-init');
      searchInput.placeholder = 'Search';
    });
  }
};
window.addEventListener('load', () => {
  setSearchInputPlaceholder();
  const algoliaForm = document.querySelector('#algolia-search-form');
  if (algoliaForm) {
    algoliaForm.addEventListener('change', (e) => {
      setSearchInputPlaceholder();
    });
  }
});
window.addEventListener('resize', () => {
  setSearchInputPlaceholder();
});
function simplifyProductTypeFacetLabels() {
  document.querySelectorAll('.ais-facet-product_type .ais-RefinementList-label')?.forEach(labelEl => {
    if (labelEl?.childNodes) {
      const fullTextNode = Array.from(labelEl.childNodes).find(node => node.nodeType === 3);
      if (fullTextNode) {
        const parts = fullTextNode.textContent.split('>').map(p => p.trim());
        if (parts.length >= 2) {
          fullTextNode.textContent = `${parts[0]} ${parts[parts.length - 1]}`;
        } else {
          fullTextNode.textContent = parts[0];
        }
      }
    }
  });
}

function observeProductTypeChanges() {
  const target = document.querySelector('.ais-facet-product_type');

  if (!target) return;

  const observer = new MutationObserver(() => {
    simplifyProductTypeFacetLabels();
  });

  observer.observe(target, {
    childList: true,
    subtree: true
  });
}

function injectLoadingOverlay() {
  if (document.getElementById('algolia-loading-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'algolia-loading-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    background: rgba(255, 255, 255, 0.9);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  spinner.style.cssText = `
    width: 40px;
    height: 40px;
    border: 4px solid #ccc;
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  overlay.appendChild(spinner);
  document.head.appendChild(style);
  document.body.appendChild(overlay);
}

function showLoadingOverlay() {
  const overlay = document.getElementById('algolia-loading-overlay');
  if (overlay) overlay.style.display = 'flex';
}

function hideLoadingOverlay() {
  const overlay = document.getElementById('algolia-loading-overlay');
  if (overlay) overlay.style.display = 'none';
}

function enhanceAlgoliaFacetsWithIcons() {
  document.querySelectorAll('.ais-facet-dropdown-wrapper').forEach(wrapper => {
    const isVendor = wrapper.classList.contains('ais-facet-vendor');
    const checkbox = wrapper.querySelector('.ais-dropdown-checkbox');
    const label = wrapper.querySelector('.ais-facet--header');
    const container = wrapper.querySelector('.ais-facet-dropdown-container');

    if (!checkbox || !label || !container) return;

    checkbox.checked = isVendor;

    let icon = label.querySelector('.facet-toggle-icon');
    if (!icon) {
      icon = document.createElement('span');
      icon.className = 'facet-toggle-icon';
      label.appendChild(icon);
    }

    const updateAccordion = () => {
      const isOpen = checkbox.checked;
      icon.textContent = isOpen ? '−' : '+';
      container.style.display = isOpen ? 'block' : 'none';
    };

    updateAccordion();

    checkbox.removeEventListener('change', updateAccordion);
    checkbox.addEventListener('change', updateAccordion);
  });
}

const SEARCH_DEBOUNCE_MS = 300;

// Oct 2026: number of products shown in the autocomplete panel. The Algolia app's
// default (config.products_autocomplete_hits_per_page) is 4 and is read once at init,
// so we override it through the app's supported 'beforeAutocompletePluginOptions' hook.
const AUTOCOMPLETE_PRODUCTS_HITS = 6;

function debounceAutocompleteSources(getSources, sourceContext) {
    let timer;
    return function (params) {
        clearTimeout(timer);
        const context = sourceContext || this;
        // Focus/clear should show empty-query results immediately.
        if (!params?.query) return getSources.call(context, params);
        return new Promise((resolve, reject) => {
            timer = setTimeout(() => {
                try {
                    resolve(getSources.call(context, params));
                } catch (error) {
                    reject(error);
                }
            }, SEARCH_DEBOUNCE_MS);
        });
    };
}

export function initAlgolia(showSpinner = true) {
    // ---- Header / home page Autocomplete (#algolia-search-form) ----
    // Registered BEFORE the InstantSearch early-return below so it also runs on pages
    // without InstantSearch (e.g. the home page). The as-you-type panel is built by the
    // Algolia Shopify app via @algolia/autocomplete-js.
    // 1) Open on focus and let the app's existing sources handle an empty query.
    //    Debounce typed queries in both the main sources and plugins.
    //    Reuse the app's templates and tracking; form submission is unaffected.
    algoliaShopify.hooks.registerHook(
        'beforeAutocompleteOptions',
        function (options) {
            options.openOnFocus = true;
            options.stallThreshold = Math.max(options.stallThreshold || 0, SEARCH_DEBOUNCE_MS + 300);
            const originalGetSources = options.getSources;
            if (typeof originalGetSources === 'function') {
                options.getSources = debounceAutocompleteSources(originalGetSources);
            }
            if (Array.isArray(options.plugins)) {
                options.plugins = options.plugins.map((plugin) => {
                    if (plugin && typeof plugin.getSources === 'function') {
                        const pluginGetSources = plugin.getSources;
                        return {
                            ...plugin,
                            getSources: debounceAutocompleteSources(pluginGetSources, plugin),
                        };
                    }
                    return plugin;
                });
            }
            return options;
        }
    );

    // 2) Show 6 products (instead of the app default of 4) in the autocomplete panel.
    //    Applies to both the typed-query results and the empty-query (rules) state.
    algoliaShopify.hooks.registerHook(
        'beforeAutocompletePluginOptions',
        function (options, context) {
            if (context && context.pluginName === 'products' && options && options.searchParameters) {
                options.searchParameters.hitsPerPage = AUTOCOMPLETE_PRODUCTS_HITS;
            }
            return options;
        }
    );

    const AlgoliaInstantSearchInstance = document.getElementById('algolia-shopify-instantsearch');
    if (!AlgoliaInstantSearchInstance) {
        return;
    }

    // ---- InstantSearch (search results AND Algolia-powered collection pages) ----
    // Debounce query changes, preserving immediate initial loads, empty collection
    // queries, and facet/pagination updates for the current query.
    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchConfigurationOptions',
        function (options) {
            const originalSearchFunction = options.searchFunction;
            let searchTimer;
            let lastSearchedQuery = null;
            const runSearch = function (helper) {
                if (typeof originalSearchFunction === 'function') {
                    originalSearchFunction.call(this, helper);
                } else {
                    helper.search();
                }
            };
            options.searchFunction = function (helper) {
                clearTimeout(searchTimer);
                const query = helper.state.query || '';
                const search = () => {
                    lastSearchedQuery = query;
                    runSearch.call(this, helper);
                };
                if (query && lastSearchedQuery !== null && query !== lastSearchedQuery) {
                    searchTimer = setTimeout(search, SEARCH_DEBOUNCE_MS);
                } else {
                    search();
                }
            };
            return options;
        }
    );

    if (showSpinner) {
        injectLoadingOverlay();
        showLoadingOverlay();
    }

    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchProductTemplate',
        hitTemplate
    );

    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchStatsTemplate',
        function (defaultTemplate, data, html) {
            return `
            <strong>${algoliaShopify.helpers.formatNumber(data.nbHits)}</strong> result${data.nbHits !== 1 ? 's' : ''} found
            `;
        }
    );

    algoliaShopify.hooks.registerHook('beforeInstantSearchStartAddWidgets', function() {
        // Widget to initialize autocomplete on InstantSearch search input
        const autocompleteWidget = {
            $$type: 'custom.initAutocomplete',

            init: function({ instantSearchInstance }) {
                // Store instantSearchInstance for later use
                this.instantSearchInstance = instantSearchInstance;

                // Initialize autocomplete on the search input
                const initAutocomplete = () => {
                    const searchInput = document.querySelector('.ais-SearchBox-input');
                    if (!searchInput) {
                        setTimeout(initAutocomplete, 100);
                        return;
                    }

                    // Check if already initialized
                    if (searchInput.hasAttribute('data-autocomplete-initialized')) return;

                    // Wait for Algolia Shopify app to be ready
                    if (typeof window.algoliaShopify === 'undefined' ||
                        !window.algoliaShopify.externals ||
                        !window.algoliaShopify.externals.autocomplete) {
                        setTimeout(initAutocomplete, 100);
                        return;
                    }

                    const { autocomplete, getAlgoliaResults } = window.algoliaShopify.externals;

                    // Get search client and index name from instantSearchInstance
                    const searchClient = instantSearchInstance.client;
                    const indexName = instantSearchInstance.helper.state.index;

                    if (!searchClient || !indexName) {
                        setTimeout(initAutocomplete, 100);
                        return;
                    }

                    try {
                        // Create autocomplete instance
                        const autocompleteInstance = autocomplete({
                            container: searchInput,
                            placeholder: 'Search',
                            openOnFocus: true,
                            stallThreshold: SEARCH_DEBOUNCE_MS + 300,
                            getSources: debounceAutocompleteSources(function ({ query }) {
                                if (!query) return [];

                                return [
                                    {
                                        sourceId: 'products',
                                        getItems() {
                                            return getAlgoliaResults({
                                                searchClient,
                                                queries: [
                                                    {
                                                        indexName: indexName,
                                                        query,
                                                    },
                                                ],
                                            }).then(({ results }) => {
                                                return results[0]?.hits || [];
                                            });
                                        },
                                        templates: {
                                            item({ item, components }) {
                                                // Use the same hit template as InstantSearch
                                                return hitTemplate({ item, components });
                                            },
                                        },
                                    },
                                ];
                            }),
                            onStateChange({ state }) {
                                // Sync autocomplete query with InstantSearch when user selects an item
                                if (state.isOpen === false && state.query) {
                                    instantSearchInstance.helper.setQuery(state.query).search();
                                }
                            },
                        });

                        // Store autocomplete instance for cleanup if needed
                        searchInput.autocompleteInstance = autocompleteInstance;
                        searchInput.setAttribute('data-autocomplete-initialized', 'true');
                        console.log('Autocomplete initialized on InstantSearch input');
                    } catch (error) {
                        console.error('Error initializing autocomplete:', error);
                    }
                };

                // Initialize after a short delay to ensure DOM is ready
                setTimeout(initAutocomplete, 200);
            },

            render: function({ results }) {
                // Re-initialize if search input appears later
                const searchInput = document.querySelector('.ais-SearchBox-input');
                if (searchInput && !searchInput.hasAttribute('data-autocomplete-initialized')) {
                    this.init({ instantSearchInstance: this.instantSearchInstance });
                }
            }
        };

        const revealWidget = {
            $$type: 'custom.revealOnLoad',

            render: function({ results }) {
                if (results && results.nbHits !== undefined) {
                    AlgoliaInstantSearchInstance?.classList.add('algolia-ready');
                    hideLoadingOverlay();
                }
            }
        };

        // Custom infinite scroll widget
        const infiniteScrollWidget = {
            $$type: 'custom.infiniteScroll',

            init: function({ helper, instantSearchInstance }) {
                this.helper = helper;
                this.allHits = [];
                this.isLoading = false;
                this.observer = null;
            },

            render: function({ results }) {
                if (!results) return;

                // Find hits container
                const hitsContainer = document.querySelector('.ais-Hits-list') ||
                                    document.querySelector('[data-widget="hits"]') ||
                                    document.querySelector('.ais-Hits');

                if (!hitsContainer) return;

                const isLastPage = results.page >= results.nbPages - 1;

                // Accumulate hits
                if (results.page === 0) {
                    this.allHits = results.hits;
                } else {
                    // Only add new hits that aren't already in the list
                    const existingIds = new Set(this.allHits.map(h => h.objectID));
                    const newHits = results.hits.filter(h => !existingIds.has(h.objectID));
                    this.allHits = this.allHits.concat(newHits);
                }

                // Hide default pagination
                const pagination = document.querySelector('.ais-Pagination');
                if (pagination) {
                    pagination.style.display = 'none';
                }

                // Setup intersection observer for infinite scroll
                if (!this.observer && !isLastPage) {
                    let sentinel = document.querySelector('.infinite-scroll-sentinel');

                    if (!sentinel) {
                        sentinel = document.createElement('div');
                        sentinel.className = 'infinite-scroll-sentinel';
                        sentinel.style.height = '1px';
                        sentinel.style.visibility = 'hidden';

                        // Insert after hits container
                        hitsContainer.parentElement.insertBefore(sentinel, hitsContainer.nextSibling);
                    }

                    this.observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting && !this.isLoading && !isLastPage) {
                                this.isLoading = true;
                                this.helper.setPage(results.page + 1).search();
                                setTimeout(() => {
                                    this.isLoading = false;
                                }, 1000);
                            }
                        });
                    }, {
                        rootMargin: '300px'
                    });

                    this.observer.observe(sentinel);
                    this.sentinel = sentinel;
                }

                // Remove sentinel on last page
                if (isLastPage && this.sentinel) {
                    if (this.observer) {
                        this.observer.disconnect();
                    }
                    this.sentinel.remove();
                }
            }
        };

        return [
            facetStateSanitizeWidget,
            suggestedRefinementWidget,
            customerPreferencesWidget,
            autocompleteWidget,
            revealWidget,
            //infiniteScrollWidget
        ];
    });

    algoliaShopify.hooks.registerHook(
        'beforeInstantSearchFacetParamsOptions',
        function (params, facet) {
            return enhanceFacetParams(params, facet);
        }
    );

    setTimeout(() => {
        enhanceAlgoliaFacetsWithIcons();
        initAlgoliaMobileDrawer();
        observeProductTypeChanges();
        initRefinementListFade();
    }, 600);
}

initRefinementListFade();

document.addEventListener('algolia.hooks.initialize', initAlgolia);
